import { useState, useEffect, useRef } from 'react';
import Icon from "../../Icon/Icon";
import PropTypes from 'prop-types';
import { findIndex, isEqual, find, map } from 'lodash';
import cn from "../../../../utils/cn";
import Browser from "../../../utils/Browser";
import { createFilterId } from "../Filter/utils";
import { formatDropdownOptions } from "./utils";
import FilterOption from "./components/FilterOption";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const FilterSingle = _ref => {
  let {
    className = '',
    name,
    uniqIdPrefix = '',
    onFilterChange,
    options: initialOptions,
    value: initialValue,
    dataTestId = '',
    prefixLabel = '',
    placeholder = 'Filter'
  } = _ref;
  const [open, setOpen] = useState(false);
  const options = formatDropdownOptions(initialOptions);
  const [value, setValue] = useState(initialValue);
  const filterWrapperRef = useRef(null);
  const id = createFilterId(name);
  useEffect(() => {
    const onClickOutside = e => {
      if (open) {
        const path = Browser.getEventPath(e);
        const index = findIndex(path, filterWrapperRef.current);
        if (index < 0) {
          setOpen(false);
        }
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [open]);
  useEffect(() => {
    if (!isEqual(initialValue, value)) {
      setValue(initialValue);
    }
  }, [initialValue, value]);
  const onButtonClick = e => {
    e.preventDefault();
    setOpen(!open);
  };
  const onOptionChange = (selectedFilter, newValue) => {
    onFilterChange(selectedFilter, newValue);
    setValue(newValue);
    setOpen(false);
    setTimeout(() => {
      document.getElementById(`${id}__button`)?.focus();
    }, 0);
  };
  const onClearClick = e => {
    e.stopPropagation();
    onFilterChange({
      label: '',
      value: ''
    });
    setValue('');
  };
  const activeFilter = find(options, {
    value
  });
  const optionElements = map(options, option => /*#__PURE__*/_jsx(FilterOption, {
    id: `${uniqIdPrefix}${name}-${option.value}`,
    isActive: activeFilter?.value === option.value,
    name: name,
    onChange: onOptionChange,
    option: option
  }, `${name}-${option.value}`));
  const filterClass = cn({
    'max-w-350px min-w-250px relative text-text-blue': true
  }, className);
  const filterButtonClass = cn('font-regular-font text-sm block h-10 pl-2.5 py-0 relative text-left select-none w-full rounded border border-solid light-border-blue', {
    'border border-united-blue overflow-hidden': open
  }, {
    'bg-dark-fill-blue hover:bg-light-border-blue border-medium-border-blue': !open && value
  }, {
    'hover:bg-medium-fill-grey bg-white border-light-border-blue': !open && !value
  });
  const dropdownClass = cn({
    'block bg-white absolute bg-gradient-to-t from-white to-light-border-grey rounded border border-solid border-action-blue z-dropdown overflow-hidden mt-2 w-full': open,
    'hidden border-light-border-blue left-0 right-0 top-full border-t-0 border': !open
  });
  const checkIconClass = cn({
    'inline-block mr-2': true
  }, {
    hidden: !value
  });
  const label = activeFilter ? prefixLabel ? `${prefixLabel}${activeFilter.label}` : activeFilter.label : placeholder;
  return /*#__PURE__*/_jsxs("div", {
    className: filterClass,
    id: id,
    ref: filterWrapperRef,
    children: [/*#__PURE__*/_jsx("button", {
      id: `${id}__button`,
      role: "listbox",
      onClick: onButtonClick,
      className: filterButtonClass,
      "aria-label": activeFilter ? `${label} selected, dropdown` : 'Filter dropdown',
      "data-testid": dataTestId,
      children: /*#__PURE__*/_jsxs("div", {
        className: "flex items-center",
        children: [/*#__PURE__*/_jsx("span", {
          className: checkIconClass,
          children: /*#__PURE__*/_jsx(Icon, {
            className: "mb-1",
            viewBox: "0 0 20 20",
            color: "brand-blue",
            icon: "Check",
            size: 14
          })
        }), /*#__PURE__*/_jsx("span", {
          className: "flex-grow",
          children: label
        }), /*#__PURE__*/_jsxs("div", {
          className: "flex items-center gap-2 mr-2",
          children: [/*#__PURE__*/_jsx(Icon, {
            icon: "Cross",
            size: 8,
            color: "medium-grey",
            onClick: onClearClick,
            className: cn('cursor-pointer hover:text-united-blue transition-colors', {
              hidden: !value
            }),
            ariaLabel: "Clear selection",
            disabled: !value
          }), /*#__PURE__*/_jsx(Icon, {
            className: "pointer-events-none",
            viewBox: "0 0 20 20",
            icon: "ChevronArrow",
            direction: open ? "up" : "down",
            size: 12,
            color: 'medium-grey'
          })]
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      className: dropdownClass,
      children: /*#__PURE__*/_jsx("div", {
        className: "overflow-y-auto select-none max-h-450px",
        children: /*#__PURE__*/_jsx("div", {
          className: "app-components-filter-options__container",
          children: optionElements
        })
      })
    })]
  });
};
FilterSingle.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.string.isRequired,
  uniqIdPrefix: PropTypes.string,
  dataTestId: PropTypes.string,
  prefixLabel: PropTypes.string,
  placeholder: PropTypes.string
};
export default FilterSingle;
//# sourceMappingURL=FilterSingle.js.map